import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import styled from '@emotion/styled'
import ChangeCircleIcon from '@mui/icons-material/ChangeCircle'
import SendIcon from '@mui/icons-material/Send'
import { LoadingButton } from '@mui/lab'
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField
} from '@mui/material'
import { useMutation } from '@tanstack/react-query'
import EsimAPI from 'api/faber/EsimAPI'
import ReplacementAPI from 'api/faber/ReplacementAPI'
import WinbackAPI from 'api/faber/WinbackAPI'
import DemoDatePicker from 'common/AbstractSingleDatePicker'
import CrossDialogBtn from 'common/Button/CrossDialogBtn'
import NormalText from 'common/Typography/NormalText'
import TextColorFontBody from 'common/Typography/TextColorFontBody'
import circlesOrbitColorPalette from 'common/ui/circlesColorTheme'
import OrbitSession from 'hooks/useCookies'
import { SubscriptionsTableRows } from 'types/api/faber/subscription.type'

import { TransformedFaberAndGlobalUsersType } from '../../OTPacks/Global/components/UserProfileV3'

import { PaperComponent } from './UpdateFaberUserDetailsDialog'

const StyledIcon = styled(Box)({
  backgroundColor: circlesOrbitColorPalette.toolkit.main,
  height: '10vh',
  width: '10vh',
  borderRadius: '50%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  boxShadow: 'rgba(0, 0, 0, 0.1) 0px 10px 50px',
  border: 'none'
})

function EsimRequestV2Dialog({
  handleRequestEsimDialogClose,
  requestEsimDialog,
  customerRow,
  faberToken,
  productType,
  updateUserDetails,
  isExternal,
}: RequestDialogProps) {
  const [remarks, setRemarks] = useState<string>('')
  const [disable, setDisable] = useState<boolean>(true)
  const userEmail = OrbitSession.get('email')
  /*HANDLE FORM INPUT */
  const handleEsimRequestRemarks = (
    evt: React.ChangeEvent<HTMLInputElement>
  ) => {
    handleEsimRequestRemarks
    evt.preventDefault()
    const newValue: string = evt.target.value
    setRemarks(newValue)
    if (evt.target.value.length < 1) {
      setDisable(true)
    } else {
      setDisable(false)
    }
  }

  /*API CALLS */
  /*TO POST ADDITIONAL DATA*/
  const { mutate: updateBonusDataPlans } = useMutation(
    isExternal ? WinbackAPI.externalUpdateBonusDataPlans : WinbackAPI.updateBonusDataPlans
  )

  function addAdditionalDataConfirm() {
    const userId = 'userID' in customerRow ? customerRow.userID : ''

    updateBonusDataPlans({
      userID: userId ? userId : '',
      packageTypeID: '315390',
      remarks: 'Add extra 20gb for Pro Pac user: Esim Replacement',
      faberToken: faberToken
    })
  }

  /*TO POST ESIM REPLACMENT*/
  const { mutate: mutationReplaceEsim, isLoading: isReplacingLoading } =
    useMutation(isExternal ? ReplacementAPI.externalEsimReplacement : ReplacementAPI.esimReplacement, {
      onSuccess: () => {
        const cycleCount =
          'cycleCount' in customerRow ? customerRow.cycleCount : 0
        const planName = 'planName' in customerRow ? customerRow.planName : ''

        toast.success('New eSim has been requested for selected customer')
        if (Number(cycleCount) === 1 && planName === 'Pro Pac') {
          addAdditionalDataConfirm()
        }
        updateUserDetails()
      },
      onError: () => {
        toast.error('Error requesting for new esim')
      },
      onSettled: () => {
        handleRequestEsimDialogClose()
      }
    })

  const { mutate: replaceOTPesim, isLoading: isReplaceOTPesimLoading } =
    useMutation(isExternal ? ReplacementAPI.externalEsimOTPReplacement : ReplacementAPI.esimOTPReplacement, {
      onSuccess: () => {
        toast.success('New eSim has been requested for selected customer')
        updateUserDetails()
      },
      onError: () => {
        toast.error('Error requesting for new esim')
      },
      onSettled: () => {
        handleRequestEsimDialogClose()
      }
    })

  const { mutate: resendEsimEmail, isLoading: isEmailSendingLoading } =
    useMutation(isExternal ? EsimAPI.externalResendEsimEmail : EsimAPI.resendEsimEmail, {
      onSuccess: () => {
        toast.success('Resent esim subscription email')
      },
      onError: () => {
        toast.error('Error resending email')
      },
      onSettled: () => {
        handleRequestEsimDialogClose()
      }
    })
  const handleConfirmRequest = (actionEvent: string) => {
    const userID = customerRow.userID ? customerRow.userID : ''

    if (actionEvent === 'replace') {
      if (productType === 'otp') {
        replaceOTPesim({
          userId: userID,
          remarks: remarks,
          faberToken: faberToken
        })
      } else {
        mutationReplaceEsim({
          userId: userID,
          remarks: remarks,
          faberToken: faberToken
        })
      }
    } else
      resendEsimEmail({
        userId: userID,
        faberToken: faberToken
      })
  }

  useEffect(() => {
    setRemarks('')
    setDisable(true)
  }, [requestEsimDialog])

  return (
    <Dialog
      open={requestEsimDialog}
      onClose={() => {
        handleRequestEsimDialogClose()
      }}
      fullWidth={true}
      maxWidth='md'
      PaperComponent={PaperComponent}
      aria-labelledby='draggable-dialog-title'
    >
      <DialogTitle>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            mb: '-15px',
            mt: '-5px'
          }}
        >
          <CrossDialogBtn dialogClose={handleRequestEsimDialogClose} />
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-evenly'
          }}
        >
          <Stack
            sx={{
              alignItems: 'center',
              maxWidth: '400px'
            }}
          >
            <StyledIcon>
              <ChangeCircleIcon
                sx={{
                  fontSize: '3em',
                  color: circlesOrbitColorPalette.primary.dark
                }}
              />
            </StyledIcon>
            <NormalText
              isBold
              variant='h6'
              sx={{ borderBottom: '1px solid grey' }}
            >
              Replace eSIM
            </NormalText>
            <Box sx={{ mt: 2 }}>
              <TextColorFontBody sx={{ fontSize: '16px' }}>
                This action will <b> REPLACE </b> customer&apos;s current eSIM
                with a <b>NEW</b> eSIM and send a new eSIM activation email to
                customer. Fill in the following form to enable eSIM replacement.
              </TextColorFontBody>
            </Box>
            <Box
              component='form'
              sx={{
                rowGap: '1em',
                columnGap: '1em',
                gridTemplateColumns: 'repeat(2, 1fr)',
                my: '20px',
                height: '15vh'
              }}
              display='grid'
              noValidate
              autoComplete='off'
            >
              <DemoDatePicker label='Date' disabled={true} />

              <TextField
                id='Requested By'
                label='Requested By'
                defaultValue={userEmail}
                variant='standard'
                disabled
              />
              <TextField
                required
                id='Remarks'
                label='Remarks'
                defaultValue=''
                variant='standard'
                onChange={handleEsimRequestRemarks}
                error={!remarks}
                helperText={!remarks && 'This is a required field'}
              />
            </Box>
            <LoadingButton
              loading={isReplacingLoading || isReplaceOTPesimLoading}
              loadingIndicator={<CircularProgress size={20} />}
              variant='contained'
              disabled={disable}
              onClick={() => handleConfirmRequest('replace')}
              sx={{
                '&:disabled': {
                  border: 'none'
                }
              }}
            >
              Replace eSIM
            </LoadingButton>
          </Stack>

          <>
            <Divider variant='middle' orientation='vertical' flexItem />
            <Stack
              sx={{
                alignItems: 'center',
                maxWidth: '400px'
              }}
            >
              <StyledIcon>
                <SendIcon
                  sx={{
                    fontSize: '3em',
                    color: circlesOrbitColorPalette.primary.dark
                  }}
                />
              </StyledIcon>
              <NormalText
                isBold
                textAlign='center'
                variant='h6'
                sx={{ borderBottom: '1px solid grey' }}
              >
                Resend eSIM activation email
              </NormalText>
              <Box sx={{ mt: 2 }}>
                <TextColorFontBody sx={{ fontSize: '16px' }}>
                  This action will only resend the eSIM activation email for
                  customer&apos;s <b>EXISTING</b> eSIM. It will <b> NOT </b>
                  replace the current eSIM.
                </TextColorFontBody>
              </Box>
              <Box sx={{ my: 2, height: '18vh' }} />
              <LoadingButton
                loading={isEmailSendingLoading}
                loadingIndicator={<CircularProgress size={20} />}
                variant='contained'
                onClick={() => handleConfirmRequest('resend')}
              >
                Resend email
              </LoadingButton>
            </Stack>
          </>
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default EsimRequestV2Dialog
interface RequestDialogProps {
  handleRequestEsimDialogClose: () => void
  requestEsimDialog: boolean
  customerRow: SubscriptionsTableRows | TransformedFaberAndGlobalUsersType
  faberToken: string
  productType?: string
  updateUserDetails: () => void
  isExternal: boolean
}
